// Auto zoom for the admit-card QR scanner. A QR held far from the camera is
// too few pixels to decode, so while nothing has been read the scanner steps
// through zoom levels and then starts over (so a close QR is read again too).
//
// Uses the camera's own zoom when the browser offers it (e.g. Chrome on
// Android). Otherwise it zooms digitally: asks for a higher resolution and
// scans a smaller centre crop of the frame at full detail.

// How long each zoom level is tried before moving on. The unzoomed level is
// held longer, as most QRs are read there.
const FIRST_STEP_MS = 2500;
const STEP_MS = 1500;

// Camera zoom factors tried, limited to what the camera supports.
const CAMERA_ZOOM_LEVELS = [1.5, 2, 2.5, 3, 4];

// Digital zoom: share of the frame's short side that is scanned. The first is
// the normal, unzoomed region.
export const DEFAULT_CROP = 0.9;
const DIGITAL_CROPS = [DEFAULT_CROP, 0.6, 0.45];

const currentTrack = (video) =>
  video.srcObject instanceof MediaStream ? video.srcObject.getVideoTracks()[0] || null : null;

const capabilitiesOf = (track) => {
  try {
    return track?.getCapabilities?.() || {};
  } catch (e) {
    return {};
  }
};

// Starts stepping through zoom levels. `setCrop(crop)` must make the scanner
// read that share of the frame; `onZoomChange(factor)` reports the level shown
// to the verifier. Returns a function that stops it.
export function startAutoZoom({ scanner, video, setCrop, onZoomChange }) {
  const track = currentTrack(video);
  const zoomCap = capabilitiesOf(track).zoom;
  const cameraZoom = Boolean(zoomCap && zoomCap.max > zoomCap.min);

  const levels = cameraZoom
    ? [zoomCap.min, ...CAMERA_ZOOM_LEVELS.filter((z) => z > zoomCap.min && z <= zoomCap.max)]
    : DIGITAL_CROPS;

  // Turning the torch off restarts the camera, which gives a new track with
  // the zoom reset, so each new track is set up again.
  const preparedTracks = new WeakSet();
  const prepareTrack = (t) => {
    if (preparedTracks.has(t)) return;
    preparedTracks.add(t);
    const caps = capabilitiesOf(t);
    const advanced = [];
    if (caps.focusMode?.includes("continuous")) advanced.push({ focusMode: "continuous" });
    if (advanced.length) t.applyConstraints({ advanced }).catch(() => {});
    // More pixels make the digital crop sharper.
    if (!cameraZoom) {
      t.applyConstraints({ width: { ideal: 1920 }, height: { ideal: 1080 } }).catch(() => {});
    }
  };

  const applyLevel = (index) => {
    const t = currentTrack(video);
    if (t) prepareTrack(t);
    if (cameraZoom) {
      if (!t) return;
      const level = levels[index];
      // Keep the torch on when it is: a constraint set replaces the previous one.
      const constraint = scanner.isFlashOn() ? { zoom: level, torch: true } : { zoom: level };
      t.applyConstraints({ advanced: [constraint] })
        .then(() => onZoomChange(level / zoomCap.min))
        .catch(() => {});
    } else {
      setCrop(levels[index]);
      onZoomChange(DEFAULT_CROP / levels[index]);
    }
  };

  let index = 0;
  let timer = null;
  const next = () => {
    index = (index + 1) % levels.length;
    applyLevel(index);
    timer = setTimeout(next, index === 0 ? FIRST_STEP_MS : STEP_MS);
  };

  applyLevel(0);
  if (levels.length > 1) timer = setTimeout(next, FIRST_STEP_MS);

  return () => clearTimeout(timer);
}
